/*
 * File: CollectionDetail.jsx
 * Project: seesaw-marketplace-app
 *
 * Created by Brendan Michaelsen on June 17, 2022 at 8:15 AM
 * Copyright © 2022 Seesaw Technologies, LLC. All rights reserved.
 *
 * Last Modified: May 18, 2023 at 12:39 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utilities
import { getDataStateForPage } from '../../../utilities/state';
import { toastError } from '../../../utilities/toaster';
import { formatCost } from '../../../utilities/cost';
import { scrollPageTo } from '../../../utilities/position';
import { formatOfferDate } from '../../../utilities/product';
import { buildDynamicMeta } from '../../../components/Meta';
import { createStateLocale } from '../../../utilities/locale';

// Services
import { updateCart } from '../../../services/cart';
import { getGroup } from '../../../services/product';

// Slices
import { pushComponentState } from '../../../store/slices/component/component.slice';
import { updateCart as updateCartSlice } from '../../../store/slices/cart/cart.slice';

// Components
import {
	Meta, AppNavigation, PageHeader, ErrorComponent, Spinner, ProductImage, Typography, SuggestedProducts, ProductCard, SchemaScript
} from '../../../components';

// Styles
import * as S from './CollectionDetail.styles';


/**
 * Component
 */

const CollectionDetail = ({
	meta, locale, merchant, data
}) => {

	// Get component parameters
	const { slug } = useParams();

	// Get state handlers
	const [pageStatus, setPageStatus] = useState('idle');
	const [componentData, setComponentData] = useState(data || { group: { name: 'Group' } });

	// Create references
	const navigationRef = useRef();

	// Use hooks
	const dispatch = useDispatch();

	// Get current component data state
	const componentState = useSelector((state) => (state.component));

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get current locale from hook
	const clientLocale = useSelector((state) => state.locale.value);
	const stateLocale = createStateLocale(clientLocale, locale);

	// Get current merchant from hook
	const clientMerchant = useSelector((state) => state.merchant.value);
	const stateMerchant = merchant || clientMerchant;

	// Handle add to cart function
	const addToCart = async (e) => {

		// Prevent default action
		e.preventDefault();

		// Update cart
		try {

			// Get group
			const group = componentData?.group;

			// Update cart
			const { data: { cart } } = await updateCart({
				...!group.isGroup ? {
					products: [{ id: group.id, add: 1 }]
				} : {
					groups: [{ id: group.id, add: 1 }]
				}
			}, stateMerchant);

			// Update cart
			dispatch(updateCartSlice(cart));

			// Open cart sidebar
			navigationRef.current.openCart();

		} catch (error) {

			// Show error message
			toastError(uiMode, stateMerchant, 'We\'re having trouble adding this collection to your cart. Give it another go.');
		}
	};

	// Initialize data
	const initializeData = async () => {

		// Get data state for page
		getDataStateForPage(componentState, setComponentData, async ({ requiresLoad, isError, data: stateData }) => {
			if (isError) {

				// Update page status
				setPageStatus('error');

			} else if (requiresLoad && pageStatus === 'idle') {

				// Update page status
				setPageStatus('loading');

				// Initialize component data
				try {
					const { data: groupData } = await getGroup(slug, stateMerchant);

					// Set component data
					setComponentData(groupData);

					// Push new component state
					dispatch(pushComponentState(groupData));

					// Update page status
					setPageStatus('success');

				} catch (e) {

					// Update page status
					setPageStatus('error');
				}
			} else if (!requiresLoad && pageStatus === 'idle') {

				// Update page status
				setPageStatus('success');

				// Initialize component data
				setComponentData(stateData);
			}
		});
	};

	// Handle component initialization
	useEffect(() => {

		// Ensure initial page loading is not complete
		if (pageStatus === 'idle') {

			// Initialize data
			initializeData();
		}
	}, [componentState, pageStatus]);

	// Handle actions on route change (dynamic page)
	useEffect(() => {

		// Only change if component state finalized
		if (componentState && componentState.status === 'final') {

			// Set page status if necessary
			if (pageStatus !== 'idle') {

				// Reset page status
				setPageStatus('idle');

			} else {

				// Initialize data
				initializeData();
			}
		}

		// Scroll page to top
		scrollPageTo(0, 0);

	}, [slug]);

	// Render component function
	const renderComponent = () => {

		// Return error component if necessary
		if (pageStatus === 'error') {
			return <ErrorComponent merchant={stateMerchant} />;
		}

		// Return loading component if necessary
		if (pageStatus === 'loading') {
			return <Spinner merchant={stateMerchant} showMeta meta={meta} />;
		}

		// Create merchant domain
		let merchantDomain = `https://${stateMerchant.domain}`;
		if (stateMerchant.redirectToSubdomain) {
			merchantDomain = `https://${stateMerchant.redirectToSubdomain}.${stateMerchant.domain}`;
		}

		// Render component
		return (
			<>
				{/* Schema.org */}
				<SchemaScript schema={{
					'@context': 'http://schema.org/',
					'@type': 'ProductCollection',
					'@id': `${merchantDomain}/collection/${componentData?.group?.slug}/#ProductCollection`,
					name: componentData?.group?.name,
					url: `${merchantDomain}/collection/${componentData?.group?.slug}`,
					image: [
						componentData?.group?.image
					],
					description: componentData?.group?.description,
					brand: {
						'@type': 'Brand',
						name: stateMerchant.name
					},
					mpn: '',
					gtin: '',
					sku: componentData?.group?.sku,
					productID: componentData?.group?.slug,
					category: componentData?.group?.typeName,
					isFamilyFriendly: true,
					offers: {
						'@type': 'Offer',
						availability: 'http://schema.org/InStock',
						price: formatCost(componentData?.group?.totalCost),
						priceCurrency: 'USD',
						itemCondition: 'https://schema.org/NewCondition',
						priceValidUntil: formatOfferDate(),
						url: `${merchantDomain}/collection/${componentData?.group?.slug}`
					},
					potentialAction: [
						{
							'@type': 'BuyAction',
							target: `${merchantDomain}/collection/${componentData?.group?.slug}`,
						},
						{
							'@type': 'ReviewAction',
							target: `${merchantDomain}/collection/${componentData?.group?.slug}`,
						},
						{
							'@type': 'ShareAction',
							target: `${merchantDomain}/collection/${componentData?.group?.slug}`,
						}
					]
				}}
				/>
				<SchemaScript schema={{
					'@context': 'http://schema.org/',
					'@type': 'ItemPage',
					'@id': `${merchantDomain}/collection/${componentData?.group?.slug}/#WebPage`,
					name: buildDynamicMeta(meta.title, componentData),
					description: buildDynamicMeta(meta.description, componentData),
					url: `${merchantDomain}/collection/${componentData?.group?.slug}`,
					inLanguage: stateLocale.localeBaseId,
					isPartOf: {
						'@id': `${merchantDomain}/#WebSite`
					},
					publisher: {
						'@id': `${stateMerchant.website}/#Organization`,
					},
					mainEntity: {
						'@id': `${merchantDomain}/collection/${componentData?.group?.slug}/#ProductCollection`,
					}
				}}
				/>

				{/* Header */}
				<PageHeader
					title={componentData?.group?.name}
					subtitle={stateMerchant.name}
				/>

				{/* Group */}
				<S.ProductWrapper>

					{/* Image */}
					<ProductImage image={componentData?.group?.image} ratio={0.7} />

					{/* Details */}
					<S.DetailContainer>

						{/* Purchase Details */}
						<S.LeftDetail>
							<Typography tag="p" variation="3" weight="light" className="secondary">
								{stateMerchant.name.toUpperCase()}
							</Typography>
							<Typography tag="h3" weight="semibold">
								{componentData?.group?.name}
							</Typography>
							<Typography tag="p" weight="light">
								Wholesale Price - $
								{formatCost(componentData?.group?.totalCost)}
							</Typography>
							<S.ButtonContainer>
								<S.Button onClick={addToCart} merchant={stateMerchant}>
									<Typography variation="button-small" weight="medium">ADD TO CART</Typography>
								</S.Button>
							</S.ButtonContainer>
							<S.MadeWith>
								<Typography tag="p" variation="2" weight="light" className="fullWidth">
									Made with
									{' '}
									<FontAwesomeIcon icon={['fa', 'heart']} className="heart" />
									{' '}
									in
									{' '}
									{stateMerchant.address?.city}
									,
									{' '}
									{stateMerchant.address?.state}
								</Typography>
							</S.MadeWith>
						</S.LeftDetail>

						{/* Group Details */}
						<S.RightDetail>
							<Typography tag="p" weight="light">{componentData?.group?.description}</Typography>
							<S.ProductList>
								<Typography tag="h4" weight="semibold">Includes</Typography>
								{componentData?.group?.products?.map((product) => (
									<S.ListItem key={product.id}>
										<Typography weight="light">{product.name}</Typography>
										<Typography weight="light">{' – '}</Typography>
										<Typography weight="semibold">{product.quantity}</Typography>
									</S.ListItem>
								))}
							</S.ProductList>
						</S.RightDetail>
					</S.DetailContainer>

					{/* Included Products */}
					<S.IncludedContainer>
						<S.CollectionDescription>
							<Typography tag="h2" weight="semibold">
								Included in this collection
							</Typography>
						</S.CollectionDescription>
						<S.ProductContainer>
							{(componentData?.group?.products || []).map((productObj) => <ProductCard product={productObj} key={productObj.id} merchant={stateMerchant} showMarkup={false} />)}
						</S.ProductContainer>
					</S.IncludedContainer>

					{/* Suggested Products */}
					<SuggestedProducts product={componentData?.group} />

				</S.ProductWrapper>
			</>
		);
	};

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={componentData} locale={stateLocale} merchant={stateMerchant} />

			{/* Component Content */}
			<AppNavigation ref={navigationRef} merchant={stateMerchant}>
				<S.Wrapper>
					{renderComponent()}
				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

CollectionDetail.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape(),
	merchant: PropTypes.shape(),
	data: PropTypes.shape(),
};
CollectionDetail.defaultProps = {
	meta: {},
	locale: {},
	merchant: {},
	data: null
};


/**
 * Exports
 */

export default CollectionDetail;
